<template>
  <div class="kb-index-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          知识库
        </h1>
        <p class="page-subtitle">
          探索技术知识，提升专业技能
        </p>
      </div>
      <!-- 搜索框 -->
      <div class="search-section">
        <div class="kb-search">
          <div class="kb-search-inner">
            <div class="kb-search-icon">
              <svg
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  d="M16.72 17.78a.75.75 0 1 0 1.06-1.06l-1.06 1.06ZM9 14.5A5.5 5.5 0 0 1 3.5 9H2a7 7 0 0 0 7 7v-1.5ZM3.5 9A5.5 5.5 0 0 1 9 3.5V2a7 7 0 0 0-7 7h1.5ZM9 3.5A5.5 5.5 0 0 1 14.5 9H16a7 7 0 0 0-7-7v1.5Zm3.89 10.45 3.83 3.83 1.06-1.06-3.83-3.83-1.06 1.06ZM14.5 9a5.48 5.48 0 0 1-1.61 3.89l1.06 1.06A6.98 6.98 0 0 0 16 9h-1.5Zm-1.61 3.89A5.48 5.48 0 0 1 9 14.5V16a6.98 6.98 0 0 0 4.95-2.05l-1.06-1.06Z"
                />
              </svg>
            </div>
            <input
              v-model="searchKeyword"
              type="text"
              class="kb-search-input"
              placeholder="搜索文章、专题..."
              @keyup.enter="handleSearch"
            >
            <button
              type="button"
              class="kb-search-button"
              @click="handleSearch"
            >
              搜索
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 分类区域 -->
    <div class="categories-section">
      <div class="section-header">
        <h2 class="section-title">
          <el-icon><Folder /></el-icon>
          技术分类
        </h2>
        <el-button 
          v-if="categories.length > itemsPerRow" 
          text 
          type="primary" 
          @click="toggleCategoriesExpand"
          class="expand-btn"
        >
          <el-icon><component :is="categoriesExpanded ? 'ArrowUp' : 'ArrowDown'" /></el-icon>
          {{ categoriesExpanded ? '收起' : '展开' }}
        </el-button>
      </div>
      <el-row
        :gutter="20"
        class="categories-grid"
        :class="{ 'collapsed': !categoriesExpanded }"
      >
        <el-col 
          :xs="12" 
          :sm="8" 
          :md="6" 
          :lg="6" 
          v-for="category in displayedCategories" 
          :key="category.id"
        >
          <el-card 
            @click="viewCategory(category.id)" 
            class="category-card"
            shadow="hover"
          >
            <div class="category-icon-wrapper">
              <el-icon
                class="category-icon"
                :size="32"
              >
                <component :is="getCategoryIcon(category.name)" />
              </el-icon>
            </div>
            <div class="category-header">
              <h3 class="category-title">
                {{ category.name }}
              </h3>
              <el-tag
                size="small"
                class="category-tag"
                :type="category.seriesCount > 0 ? 'success' : 'info'"
              >
                {{ category.seriesCount }} 个专题
              </el-tag>
            </div>
            <p class="category-desc">
              {{ category.description }}
            </p>
          </el-card>
        </el-col>
      </el-row>
      <!-- 分页组件 -->
      <div
        v-if="categoriesExpanded && categories.length > pageSize"
        class="pagination-wrapper"
      >
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[20, 40, 60, 100]"
          :total="categories.length"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 推荐专题区域 -->
    <section
      class="recommended-section"
      v-if="recommendedSeries.length > 0"
    >
      <header class="recommended-header">
        <h2 class="recommended-title">
          <span class="recommended-title-icon">
            <el-icon><Star /></el-icon>
          </span>
          推荐专题
        </h2>
        <p class="recommended-desc">
          精选系列，系统学习
        </p>
      </header>
      <div class="series-scroll-container">
        <el-row
          :gutter="24"
          class="series-grid"
        >
          <el-col
            :xs="24"
            :sm="12"
            :md="8"
            :lg="8"
            v-for="series in recommendedSeries"
            :key="series.id"
          >
            <el-card
              @click="goToSeries(series.id)"
              class="series-card"
              shadow="hover"
            >
              <div
                class="series-cover-wrapper"
                :class="{ 'no-cover': !series.coverUrl }"
              >
                <img
                  v-if="series.coverUrl"
                  :src="getCoverThumbSrc(series)"
                  class="series-cover"
                  @error="handleImageError"
                >
                <div class="series-overlay" />
                <span class="series-badge">推荐</span>
              </div>
              <div class="series-content">
                <h4 class="series-title">
                  {{ series.title }}
                </h4>
                <p class="series-summary">
                  {{ series.summary || '暂无简介' }}
                </p>
                <div class="series-footer">
                  <el-tag
                    :type="getLevelType(series.level)"
                    size="small"
                    class="level-tag"
                  >
                    {{ getLevelText(series.level) }}
                  </el-tag>
                  <span v-if="series.favoriteCount != null" class="series-fav-count">{{ series.favoriteCount }} 人收藏</span>
                  <span class="series-action">
                    进入专题
                    <el-icon class="arrow-icon"><ArrowRight /></el-icon>
                  </span>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated, computed, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search, Folder, Star, ArrowRight, Monitor, Cpu, Connection, Tools, Document } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { takePrefetchedKb } from '@/utils/kbPrefetch'
import { startKbSeriesListPrefetch } from '@/utils/kbSeriesListPrefetch'
import { getKbIndexCache, setKbIndexCache } from '@/utils/kbIndexCache'
import { setCategoriesCache } from '@/utils/kbSeriesListCache'
import { getImageUrl } from '@/utils/imageUrl'
import { getThumbUrl } from '@/utils/imageThumbCache'

const router = useRouter()

const categories = ref([])
const recommendedSeries = ref([])
const searchKeyword = ref('')
const categoriesExpanded = ref(false)
const currentPage = ref(1)
const pageSize = ref(20)
const windowWidth = ref(window.innerWidth)
const thumbSrcMap = ref({}) // seriesId -> thumb src

// 根据屏幕宽度计算每行显示的数量
const itemsPerRow = computed(() => {
  // Element Plus 响应式断点：
  // xs: < 768px (span=12 -> 2个/行)
  // sm: >= 768px (span=8 -> 3个/行)
  // md: >= 992px (span=6 -> 4个/行)
  // lg: >= 1200px (span=6 -> 4个/行)
  if (windowWidth.value < 768) {
    return 2 // xs
  } else if (windowWidth.value < 992) {
    return 3 // sm
  } else {
    return 4 // md/lg
  }
})

// 显示的分类列表
const displayedCategories = computed(() => {
  if (!categoriesExpanded.value) {
    // 收起状态：只显示第一行
    return categories.value.slice(0, itemsPerRow.value)
  } else {
    // 展开状态：显示当前页的数据
    const start = (currentPage.value - 1) * pageSize.value
    const end = start + pageSize.value
    return categories.value.slice(start, end)
  }
})

// 监听窗口大小变化
const handleResize = () => {
  windowWidth.value = window.innerWidth
  // 如果收起状态，重新计算显示数量
  if (!categoriesExpanded.value) {
    // 触发响应式更新
  }
}

const getLevelType = (level) => {
  if (typeof level !== 'number') return ''
  // 根据难度范围设置颜色：1-10=success, 11-20=warning, 21-30=danger
  if (level <= 10) return 'success'
  if (level <= 20) return 'warning'
  return 'danger'
}

const getLevelText = (level) => {
  if (typeof level === 'number') {
    return `level:${level}`
  }
  return level || '未知'
}

const getCategoryIcon = (categoryName) => {
  const iconMap = {
    '前端技术': Monitor,
    '后端技术': Cpu,
    '全栈技术': Connection,
    '运维技术': Tools,
    '测试技术': Document
  }
  return iconMap[categoryName] || Folder
}

const loadCategories = async () => {
  try {
    const data = await request.get('/kb/categories')
    categories.value = Array.isArray(data) ? data : []
    setCategoriesCache(categories.value)
  } catch (error) {
    ElMessage.error('加载分类失败')
  }
}

const loadRecommendedSeries = async () => {
  try {
    const data = await request.get('/kb/series', {
      params: { current: 1, size: 6, recommended: true }
    })
    recommendedSeries.value = data.records || []
  } catch (error) {
    ElMessage.error('加载推荐专题失败')
  }
}

/** 首次进入且无缓存时完整加载 */
async function loadKbIndexFull() {
  await Promise.all([loadCategories(), loadRecommendedSeries()])
  setKbIndexCache(categories.value, recommendedSeries.value)
}

/** 后台静默刷新，不清空界面 */
async function refreshKbIndexSilent() {
  try {
    const [cats, seriesRes] = await Promise.all([
      request.get('/kb/categories').then((d) => (Array.isArray(d) ? d : [])).catch(() => null),
      request
        .get('/kb/series', { params: { current: 1, size: 6, recommended: true } })
        .catch(() => null)
    ])
    if (cats != null) {
      categories.value = cats
      setCategoriesCache(cats)
    }
    if (seriesRes && Array.isArray(seriesRes.records)) {
      recommendedSeries.value = seriesRes.records
    }
    setKbIndexCache(categories.value, recommendedSeries.value)
  } catch (_) {
    /* 保留当前展示 */
  }
}

const viewCategory = (categoryId) => {
  router.push({ name: 'KbSeriesList', query: { categoryId } })
}

const goToSeries = (seriesId) => {
  router.push(`/kb/series/${seriesId}`)
}

const getCoverThumbSrc = (series) => {
  if (!series?.coverUrl) return ''
  return thumbSrcMap.value[series.id] || getImageUrl(series.coverUrl)
}

const handleImageError = (event) => {
  // 图片加载失败时隐藏图片元素
  event.target.style.display = 'none'
}

const handleSearch = () => {
  if (searchKeyword.value.trim()) {
    router.push({ path: '/kb/search', query: { keyword: searchKeyword.value } })
  }
}

const toggleCategoriesExpand = () => {
  categoriesExpanded.value = !categoriesExpanded.value
  if (categoriesExpanded.value) {
    // 展开时重置到第一页
    currentPage.value = 1
  }
}

const handleSizeChange = (size) => {
  pageSize.value = size
  currentPage.value = 1
}

const handlePageChange = (page) => {
  currentPage.value = page
  // 滚动到分类区域顶部
  const element = document.querySelector('.categories-section')
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// 有分类数据后预取前几个分类的专题列表，用户点击进入时 LCP 更早
const SERIES_LIST_PREFETCH_COUNT = 5
watch(
  () => categories.value.slice(0, SERIES_LIST_PREFETCH_COUNT),
  (firstCategories) => {
    if (!Array.isArray(firstCategories) || firstCategories.length === 0) return
    firstCategories.forEach((c) => {
      if (c && c.id != null) startKbSeriesListPrefetch(String(c.id))
    })
  },
  { immediate: true, deep: true }
)

// recommendedSeries 列表变化时，异步生成/读取缩略图并缓存到本地
watch(recommendedSeries, async (list) => {
  if (!Array.isArray(list) || list.length === 0) return
  const entries = await Promise.all(
    list
      .filter(s => s && s.id && s.coverUrl)
      .map(async (s) => {
        const full = getImageUrl(s.coverUrl)
        const thumb = await getThumbUrl(full, { width: 360, height: 240 })
        return [s.id, thumb]
      })
  )
  const next = { ...thumbSrcMap.value }
  for (const [id, src] of entries) next[id] = src
  thumbSrcMap.value = next
}, { immediate: true })

onMounted(async () => {
  const prefetched = await takePrefetchedKb()
  if (prefetched && (prefetched.categories?.length >= 0 || prefetched.recommendedSeries?.length >= 0)) {
    if (Array.isArray(prefetched.categories)) categories.value = prefetched.categories
    if (Array.isArray(prefetched.recommendedSeries)) recommendedSeries.value = prefetched.recommendedSeries
    setCategoriesCache(categories.value)
    setKbIndexCache(categories.value, recommendedSeries.value)
    refreshKbIndexSilent()
  } else {
    const cached = getKbIndexCache()
    if (cached && (cached.categories.length > 0 || cached.recommendedSeries.length > 0)) {
      categories.value = cached.categories
      recommendedSeries.value = cached.recommendedSeries
      refreshKbIndexSilent()
    } else {
      await loadKbIndexFull()
    }
  }
  window.addEventListener('resize', handleResize)
})

onActivated(() => {
  if (categories.value.length > 0 || recommendedSeries.value.length > 0) {
    refreshKbIndexSilent()
  } else {
    loadKbIndexFull()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped lang="scss">
.kb-index-page {
  padding: 0;
  max-width: 1400px;
  margin: 0 auto;
}

// 页面头部（游戏化：深色玻璃感 + 顶线）
.page-header {
  position: relative;
  overflow: hidden;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 40px var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  transition: box-shadow var(--transition-base) var(--ease-out);
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--panel-top-line);
  opacity: 0.9;
  pointer-events: none;
}

.page-header .header-content {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.page-header .page-title {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: var(--font-size-xxl);
  font-weight: 700;
  color: var(--color-text-primary);
  background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-primary) 50%, var(--color-primary-light) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.page-header .page-subtitle {
  margin: 0;
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
}

.page-header .search-section {
  max-width: 840px;
  margin: 0 auto;
}

.page-header .kb-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px 12px;
  border-radius: 999px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.page-header .kb-search:focus-within {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-glow);
}

.page-header .kb-search-inner {
  display: flex;
  width: 100%;
  background: var(--color-bg-elevated);
  border-radius: 999px;
  overflow: hidden;
  min-height: 48px;
  border: 1px solid transparent;
}

.page-header .kb-search-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  background: var(--color-bg-hover);
  border-right: 1px solid var(--color-border);
}

.page-header .kb-search-icon svg {
  width: 18px;
  height: 18px;
  fill: var(--color-text-secondary);
}

.page-header .kb-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.page-header .kb-search-input::placeholder {
  color: var(--color-text-tertiary);
  font-weight: 400;
}

.page-header .kb-search-button {
  border: none;
  outline: none;
  padding: 0 30px;
  background: var(--color-primary);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 0 12px 12px 0;
  transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
}

.page-header .kb-search-button:hover {
  background: var(--color-primary-hover);
  box-shadow: var(--shadow-glow);
}

.page-header .kb-search-button:active {
  transform: translateY(1px);
}

// 分类区域
.categories-section {
  margin-bottom: var(--spacing-xxl);
}

.categories-section .section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.categories-section .section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.categories-section .section-title .el-icon {
  color: var(--color-primary);
}

.categories-section .expand-btn {
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
}

.categories-section .expand-btn .el-icon {
  margin-right: var(--spacing-xs);
}
  
  .categories-grid {
    margin: 0;
    row-gap: 16px;
    transition: all var(--transition-base) var(--ease-out);
    
    &.collapsed {
      // 收起状态可以添加一些样式
    }
  }
  
  .pagination-wrapper {
    margin-top: var(--spacing-xl);
    display: flex;
    justify-content: center;
    padding: var(--spacing-lg) 0;
    
    :deep(.el-pagination) {
      .el-pagination__total,
      .el-pagination__sizes,
      .el-pagination__jump {
        color: var(--el-text-color-regular);
      }
    }
  }

.category-card {
  position: relative;
  cursor: pointer;
  margin-bottom: 28px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow var(--transition-base) var(--ease-out), border-color var(--transition-fast);
  height: 100%;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--color-border);
  background: var(--color-bg-card);
}

.category-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--panel-top-line);
  opacity: 0.7;
  pointer-events: none;
  z-index: 1;
}

.category-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-lg), var(--shadow-glow);
  border-color: var(--color-primary);
}

.category-card:hover .category-icon {
  transform: scale(1.1);
  color: var(--color-primary-light);
}

.category-card :deep(.el-card__body) {
  padding: 24px;
  background: var(--color-bg-card);
}

.category-card .category-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  transition: border-color var(--transition-fast), background var(--transition-fast);
}

.category-card:hover .category-icon-wrapper {
  border-color: rgba(59, 130, 246, 0.4);
  background: var(--color-primary-50);
}

.category-card .category-icon {
  color: var(--color-primary);
  transition: all 0.3s ease;
}

.category-card .category-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}

.category-card .category-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  flex: 1;
}

.category-card .category-tag {
  flex-shrink: 0;
  margin-top: 2px;
}

.category-card .category-desc {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

// 推荐专题区域
.recommended-section {
  margin-bottom: var(--spacing-xxl);
  padding: var(--spacing-xl) 0 0;
}

.recommended-section .recommended-header {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border);
}

.recommended-section .recommended-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.02em;
  line-height: 1.3;
}

.recommended-section .recommended-title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--color-primary-100);
  border: 1px solid rgba(59, 130, 246, 0.35);
  color: var(--color-primary-light);
  box-shadow: var(--shadow-glow);
}

.recommended-section .recommended-title-icon .el-icon {
  font-size: 20px;
}

.recommended-section .recommended-desc {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  padding-left: 52px;
}

.recommended-section .series-scroll-container {
  overflow: visible;
}

.recommended-section .series-grid {
  margin: 0 -12px;
  row-gap: 24px;
}

.series-card {
  position: relative;
  cursor: pointer;
  margin-bottom: 0;
  transition: transform var(--transition-base) var(--ease-out), box-shadow var(--transition-base) var(--ease-out), border-color var(--transition-fast) var(--ease-out);
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--color-border);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-card);
}

.series-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--panel-top-line);
  opacity: 0.7;
  pointer-events: none;
  z-index: 1;
}

.series-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg), var(--shadow-glow);
  border-color: var(--color-primary);
}

.series-card:hover .series-cover {
  transform: scale(1.06);
}

.series-card:hover .series-action .arrow-icon {
  transform: translateX(3px);
}

.series-card :deep(.el-card__body) {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.series-card .series-cover-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 200px;
  overflow: hidden;
  background: var(--color-bg-secondary);
}

.series-card .series-cover-wrapper.no-cover {
  min-height: 140px;
  max-height: none;
}

.series-card .series-cover-wrapper .series-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.series-card .series-cover-wrapper .series-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.15) 100%);
}

.series-card .series-cover-wrapper .series-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(6px);
  border-radius: 6px;
}

.series-card .series-content {
  padding: 18px 20px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.series-card .series-content .series-title {
  margin: 0 0 10px 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  letter-spacing: -0.01em;
}

.series-card .series-content .series-summary {
  margin: 0 0 14px 0;
  font-size: 13px;
  color: var(--el-text-color-regular);
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.series-card .series-content .series-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 2px;
}

.series-card .series-content .series-footer .level-tag {
  font-weight: 500;
}

.series-card .series-content .series-footer .series-fav-count {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-left: 8px;
}

.series-card .series-content .series-footer .series-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: var(--el-color-primary);
}

.series-card .series-content .series-footer .series-action .arrow-icon {
  font-size: 16px;
  transition: transform 0.2s ease;
}

// 响应式设计
@media (max-width: 768px) {
  .page-header {
    padding: 24px 16px;
    
    .header-content {
      .page-title {
        font-size: 24px;
      }
      
      .page-subtitle {
        font-size: 14px;
      }
    }
  }
  
  .category-card {
    :deep(.el-card__body) {
      padding: 16px;
    }
    
    .category-icon-wrapper {
      width: 48px;
      height: 48px;
      margin-bottom: 12px;
    }
  }
  
  .recommended-section {
    margin-bottom: 36px;
    padding-top: 24px;

    .recommended-title {
      font-size: 20px;
    }

    .recommended-title-icon {
      width: 32px;
      height: 32px;
      .el-icon { font-size: 16px; }
    }

    .recommended-desc {
      font-size: 13px;
      padding-left: 46px;
    }
  }

  .recommended-section .series-grid {
    row-gap: 20px;
  }

  .series-card {
    margin-bottom: 0;

    .series-cover-wrapper {
      max-height: 160px;

      &.no-cover {
        min-height: 120px;
      }

      .series-badge {
        top: 10px;
        left: 10px;
        padding: 3px 8px;
        font-size: 11px;
      }
    }

    .series-content {
      padding: 14px 16px 16px;

      .series-title {
        font-size: 16px;
      }

      .series-summary {
        font-size: 12px;
      }

      .series-action {
        font-size: 12px;
      }
    }
  }
}
</style>
